<template>
    <el-container>
        <el-header>
            <span>{{$t('report.headerTitle')}}</span>
        </el-header>
        <el-main class="report-main">
            <div class="report-tabs">
                <div class="report-tab-list">
                    <button :class="['report-tab', activeReportTab === 'dashboard' ? 'active' : '']" type="button" @click="changeReportTab('dashboard')">{{$t('report.tabDashboard')}}</button>
                    <button :class="['report-tab', activeReportTab === 'reportList' ? 'active' : '']" type="button" @click="changeReportTab('reportList')">{{$t('report.tabReportList')}}</button>
                </div>
                <el-select v-if="activeReportTab === 'dashboard'" class="year-select" v-model="selectedYear" @change="queryReportData">
                    <el-option
                            v-for="yearOption in yearOptions"
                            :key="yearOption.key"
                            :label="yearOption.label"
                            :value="yearOption.value"></el-option>
                </el-select>
                <button v-if="activeReportTab === 'reportList'" class="export-button" type="button" @click="exportReportListCsv">
                    {{$t('report.export')}}
                    <i class="el-icon-arrow-down"></i>
                </button>
            </div>

            <template v-if="activeReportTab === 'dashboard'">
                <section class="report-section report-overview-section" v-loading="loading">
                    <div class="report-overview-grid">
                        <div class="report-panel">
                            <h2>{{$t('report.annualSalesByClient')}}</h2>
                            <div class="client-chart-row">
                                <div class="donut-chart-wrap">
                                    <svg class="donut-chart" viewBox="0 0 200 200">
                                        <path
                                                v-for="client in clientPieSliceList"
                                                :key="'pie-' + client.innerUUID"
                                                class="donut-slice"
                                                :fill="client.color"
                                                :d="client.path"
                                                @mouseenter="showClientPieTooltip(client, $event)"
                                                @mousemove="moveClientPieTooltip($event)"
                                                @mouseleave="hideClientPieTooltip"
                                                @click="goInvoiceListByClient(client)"></path>
                                    </svg>
                                    <div
                                            v-if="clientPieTooltip.visible"
                                            class="client-pie-tooltip"
                                            :style="{left: clientPieTooltip.left + 'px', top: clientPieTooltip.top + 'px'}">
                                        {{clientPieTooltip.content}}
                                    </div>
                                </div>
                                <div class="client-sales-list">
                                    <div class="client-sales-row" v-for="client in clientSalesList" :key="client.innerUUID" @click="goInvoiceListByClient(client)">
                                        <span class="client-color" :style="{background: client.color}"></span>
                                        <span class="client-name">{{client.name}} - {{client.percent}}%</span>
                                        <strong>{{formatMoney(client.totalMoney)}}</strong>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div class="report-panel">
                            <h2>{{$t('report.totalOutstandingBalance')}}</h2>
                            <div class="outstanding-total">{{formatMoney(totalOutstandingBalance)}}</div>
                            <div class="outstanding-list">
                                <div class="outstanding-row" v-for="client in displayOutstandingBalanceList" :key="client.innerUUID" @click="goInvoiceListByClient(client)">
                                    <strong>{{client.name}}</strong>
                                    <strong>{{formatMoney(client.totalOutstandingBalance)}}</strong>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>

                <section class="report-section sales-section" v-loading="loading">
                    <div class="report-section-header">
                        <div>
                            <h2>{{saleReportTitle}}</h2>
                            <div class="invoice-count">{{reportSummary.totalInvoiceCount}} {{$t('report.invoicesCountSuffix')}}</div>
                            <div class="total-sales">{{formatMoney(reportSummary.totalMoney)}}</div>
                        </div>
                    </div>

                    <div class="report-mode-tabs">
                        <button :class="['mode-button', dateIncrement === 'month' ? 'active' : '']" type="button" @click="changeDateIncrement('month')">{{$t('report.byMonth')}}</button>
                        <button :class="['mode-button', dateIncrement === 'quarter' ? 'active' : '']" type="button" @click="changeDateIncrement('quarter')">{{$t('report.byQuarter')}}</button>
                    </div>

                    <div v-if="displayPeriodList.length > 0" class="bar-chart-wrap">
                        <div v-if="selectedYear === null && displayPeriodList.length > 10" class="bar-chart-actions">
                            <el-button size="mini" icon="el-icon-arrow-left" circle @click="scrollSaleChart(-1)"></el-button>
                            <el-button size="mini" icon="el-icon-arrow-right" circle @click="scrollSaleChart(1)"></el-button>
                        </div>
                        <div ref="saleChartScroll" class="bar-chart-scroll" :style="{width: chartViewportWidth}" @wheel="handleSaleChartWheel">
                            <div class="bar-chart" :style="{gridTemplateColumns: chartGridTemplateColumns, width: chartWidth}">
                                <div
                                        v-for="period in displayPeriodList"
                                        :key="period.periodKey"
                                        class="bar-column">
                                    <el-tooltip effect="dark" :content="getSaleTooltip(period)" placement="top">
                                        <div class="bar-track">
                                            <div class="bar-value" :style="{height: getBarHeight(period.totalMoney)}"></div>
                                        </div>
                                    </el-tooltip>
                                    <div class="bar-label">{{period.periodName}}</div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div v-else class="empty-report">{{$t('report.noSalesFound')}}</div>

                    <div class="period-list">
                                    <div class="period-row" v-for="period in displayPeriodTableList" :key="'row-' + period.periodKey" @click="goInvoiceListBySalePeriod(period)">
                                        <span>{{period.title}} - {{period.invoiceCount}} {{$t('report.invoicesCountSuffix')}}</span>
                                        <strong>{{formatMoney(period.totalMoney)}}</strong>
                                    </div>
                    </div>
                </section>
            </template>

            <template v-else>
                <section class="report-list-section" v-loading="loading">
                    <div class="report-list-filters">
                        <div class="report-list-filter">
                            <span>{{$t('report.filterReportTypes')}}</span>
                            <el-select class="report-list-select" v-model="reportListType" @change="changeReportListType">
                                <el-option
                                        v-for="reportTypeOption in reportTypeOptions"
                                        :key="reportTypeOption.value"
                                        :label="reportTypeOption.label"
                                        :value="reportTypeOption.value"></el-option>
                            </el-select>
                        </div>
                        <div v-if="showReportListTimePeriodFilter" class="report-list-filter">
                            <span>{{$t('report.filterTimePeriod')}}</span>
                            <el-select class="report-list-select" v-model="selectedReportListYear" @change="queryReportListData">
                                <el-option
                                        v-for="yearOption in yearOptions"
                                        :key="'report-list-year-' + yearOption.key"
                                        :label="yearOption.label"
                                        :value="yearOption.value"></el-option>
                            </el-select>
                        </div>
                        <div v-if="showReportListGroupTypeFilter" class="report-list-filter">
                            <span>{{$t('report.filterView')}}</span>
                            <el-select class="report-list-select" v-model="reportListDateIncrement" @change="queryReportListData">
                                <el-option :label="$t('report.optionMonthly')" value="month"></el-option>
                                <el-option :label="$t('report.optionQuarterly')" value="quarter"></el-option>
                            </el-select>
                        </div>
                    </div>

                    <div class="report-list-summary">
                        <div v-if="isReportListJournal" class="report-list-year-title">{{reportListJournalTitle}}</div>
                        <template v-else-if="reportListType === 'CUSTOMER_AGING'">
                            <div class="report-list-year-title">{{$t('report.customerAgingTitle')}}</div>
                        </template>
                        <template v-else>
                            <div class="invoice-count">{{reportListSummary.summaryCount}} {{reportListSummary.summaryCountName}}</div>
                            <div class="total-sales">{{formatReportListMoney(reportListSummary.summaryTotalMoney)}}</div>
                        </template>
                    </div>

                    <div v-if="reportListChartType === 'BAR' && reportListChartList.length > 0" class="bar-chart-wrap report-list-chart-wrap">
                        <div v-if="reportListTimePeriodType === 'ALL_TIME' && reportListChartList.length > 10" class="bar-chart-actions">
                            <el-button size="mini" icon="el-icon-arrow-left" circle @click="scrollReportListChart(-1)"></el-button>
                            <el-button size="mini" icon="el-icon-arrow-right" circle @click="scrollReportListChart(1)"></el-button>
                        </div>
                        <div ref="reportListSaleChartScroll" class="bar-chart-scroll" :style="{width: chartViewportWidth}" @wheel="handleReportListChartWheel">
                            <div class="bar-chart" :style="{gridTemplateColumns: reportListChartGridTemplateColumns, width: reportListChartWidth}">
                                <div
                                        v-for="period in reportListChartList"
                                        :key="'report-list-chart-' + period.periodName"
                                        class="bar-column">
                                    <el-tooltip effect="dark" :content="getReportListSaleTooltip(period)" placement="top">
                                        <div class="bar-track">
                                            <div class="bar-value" :style="{height: getReportListBarHeight(period.totalMoney)}"></div>
                                        </div>
                                    </el-tooltip>
                                    <div class="bar-label">{{getReportListChartLabel(period)}}</div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div v-else-if="reportListChartType === 'DONUT' && reportListChartList.length > 0" class="report-list-donut-row">
                        <div class="report-list-donut-wrap">
                            <svg class="report-list-donut" viewBox="0 0 200 200">
                                <path
                                        v-for="chartItem in reportListDonutSliceList"
                                        :key="'report-list-donut-' + chartItem.index"
                                        class="donut-slice"
                                        :fill="chartItem.color"
                                        :d="chartItem.path"
                                        @click="goInvoiceListByReportClient(chartItem)"></path>
                            </svg>
                        </div>
                        <div class="report-list-donut-legend">
                            <div class="report-list-donut-row-item" v-for="chartItem in reportListChartLegendList" :key="'report-list-legend-' + chartItem.index" @click="goInvoiceListByReportClient(chartItem)">
                                <span class="client-color" :style="{background: chartItem.color}"></span>
                                <strong>{{chartItem.name}} - {{chartItem.percent}}%</strong>
                                <strong>{{formatReportListMoney(chartItem.totalMoney)}}</strong>
                            </div>
                        </div>
                    </div>
                    <div v-else-if="reportListLoaded && showReportListEmptyChart" class="empty-report">{{$t('report.noDataForDateRange')}}</div>
                </section>

                <section v-if="reportListLoaded" class="report-detail-section" v-loading="loading">
                    <div class="report-detail-table-wrap">
                        <table :class="['report-detail-table', reportListType === 'CUSTOMER_AGING' ? 'customer-aging-table' : '', reportListType === 'SALES_JOURNAL' ? 'sales-journal-table' : '', reportListType === 'PAYMENTS_JOURNAL' ? 'payments-journal-table' : '']">
                            <colgroup v-if="reportListType === 'CUSTOMER_AGING'">
                                <col class="customer-aging-client-col">
                                <col>
                                <col>
                                <col>
                                <col>
                                <col>
                                <col>
                            </colgroup>
                            <thead>
                            <tr>
                                <th v-for="column in reportListColumnList" :key="'report-list-head-' + column.key" @click="changeReportListSort(column)">
                                    <span class="list-sort-header" :class="{'is-active': reportListSortKey === column.key}">
                                        {{column.label}}
                                        <svg v-if="reportListSortKey === column.key" class="list-sort-icon is-active" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                                            <path v-if="reportListSortOrder === 1" d="M8 6L12 2L16 6"/>
                                            <path v-else d="M8 18L12 22L16 18"/>
                                            <path d="M12 2V22"/>
                                        </svg>
                                    </span>
                                </th>
                            </tr>
                            </thead>
                            <tbody>
                            <tr
                                    v-for="(row, rowIndex) in sortedReportListDetailList"
                                    :key="'report-list-detail-' + rowIndex"
                                    :class="{'report-list-clickable-row': reportListType === 'SALES_BY_CLIENT'}"
                                    @click="goInvoiceListByReportClientRow(row)">
                                <td v-for="column in reportListColumnList" :key="'report-list-cell-' + rowIndex + '-' + column.key">
                                    {{formatReportListCell(row, column)}}
                                </td>
                            </tr>
                            <tr class="report-detail-total-row">
                                <td v-for="column in reportListColumnList" :key="'report-list-total-' + column.key">
                                    {{formatReportListCell(reportListTotalRow, column)}}
                                </td>
                            </tr>
                            </tbody>
                        </table>
                    </div>
                </section>
            </template>

        </el-main>
    </el-container>
</template>

<script>
    import {xiaoshudianfenge} from '@/utils/moneyFormat'
    import {getCurrentCompanyUUID} from '@/utils/company'

    export default {
        data() {
            return {
                activeReportTab: "dashboard",
                loading: false,
                selectedYear: null,
                dateIncrement: "month",
                selectedReportListYear: null,
                reportListDateIncrement: "month",
                reportListType: "SALES_BY_DATE",
                userSetting: JSON.parse(localStorage.getItem('userSetting')) || {},
                reportSummary: {
                    totalInvoiceCount: 0,
                    totalMoney: 0
                },
                reportListSummary: {
                    reportType: "SALES_BY_DATE",
                    currencyName: "",
                    summaryCount: 0,
                    summaryCountName: "",
                    summaryTotalMoney: 0
                },
                reportBarList: [],
                reportListChartList: [],
                reportListDetailList: [],
                reportListTotalRow: {},
                reportListChartType: "BAR",
                reportListSortKey: "",
                reportListSortOrder: "",
                reportListLoaded: false,
                clientSalesList: [],
                outstandingBalanceList: [],
                loadingCount: 0,
                clientPieTooltip: {
                    visible: false,
                    content: "",
                    left: 0,
                    top: 0
                },
                clientColorList: ["#217BD3", "#31A66A", "#F2A541", "#E05252", "#6B5DD3", "#00A7A7", "#7A5230", "#8C8C8C"],
                reportListColorList: ["#8BE6CB", "#8FC5FF", "#FFF1A6", "#FFB56B", "#EB6A6A", "#8C7BFF", "#65D2D2", "#9B9B9B"]
            }
        },
        computed: {
            reportTypeOptions() {
                return [
                    {label: this.$t('report.typeSalesByDate'), value: "SALES_BY_DATE"},
                    {label: this.$t('report.typeSalesByClient'), value: "SALES_BY_CLIENT"},
                    {label: this.$t('report.typeSalesByItem'), value: "SALES_BY_ITEM"},
                    {label: this.$t('report.typeSalesJournal'), value: "SALES_JOURNAL"},
                    {label: this.$t('report.typePaymentsByDate'), value: "PAYMENTS_BY_DATE"},
                    {label: this.$t('report.typePaymentsByClient'), value: "PAYMENTS_BY_CLIENT"},
                    {label: this.$t('report.typePaymentsByType'), value: "PAYMENTS_BY_TYPE"},
                    {label: this.$t('report.typePaymentsJournal'), value: "PAYMENTS_JOURNAL"},
                    {label: this.$t('report.typeExpenseByCategory'), value: "EXPENSE_BY_CATEGORY"},
                    {label: this.$t('report.typeExpenseJournal'), value: "EXPENSE_JOURNAL"},
                    {label: this.$t('report.typeCustomerAging'), value: "CUSTOMER_AGING"}
                ];
            },
            yearOptions() {
                let yearList = [{
                    key: "all",
                    label: this.$t('report.allYears'),
                    value: null
                }];
                let currentYear = this.getCurrentFiscalYearEnd();
                for (let i = currentYear + 1; i >= 2018; i--) {
                    yearList.push({
                        key: String(i),
                        label: String(i),
                        value: i
                    });
                }
                return yearList;
            },
            showReportListTimePeriodFilter() {
                return this.reportListType !== "CUSTOMER_AGING";
            },
            reportListTimePeriodType() {
                if (this.selectedReportListYear === null) {
                    return "ALL_TIME";
                }
                return "TAX_YEAR";
            },
            showReportListGroupTypeFilter() {
                return this.reportListType === "SALES_BY_DATE" || this.reportListType === "PAYMENTS_BY_DATE";
            },
            isReportListJournal() {
                return this.reportListType === "SALES_JOURNAL"
                    || this.reportListType === "PAYMENTS_JOURNAL"
                    || this.reportListType === "EXPENSE_JOURNAL";
            },
            reportListJournalTitle() {
                if (this.selectedReportListYear === null) {
                    return this.$t('report.allTime');
                }
                return String(this.selectedReportListYear);
            },
            saleReportTitle() {
                if (this.selectedYear === null) {
                    return this.$t('report.allSales');
                }
                return this.$t('report.taxYearSales', {year: this.selectedYear});
            },
            groupType() {
                if (this.dateIncrement === "month") {
                    return "MONTH";
                }
                if (this.dateIncrement === "quarter") {
                    return "QUARTER";
                }
                throw new Error("Invalid report group type");
            },
            reportListGroupType() {
                if (this.reportListDateIncrement === "month") {
                    return "MONTH";
                }
                if (this.reportListDateIncrement === "quarter") {
                    return "QUARTER";
                }
                throw new Error("Invalid report list group type");
            },
            reportListFirstColumnLabel() {
                if (this.reportListGroupType === "MONTH") {
                    return this.$t('report.firstColumnMonth');
                }
                return this.$t('report.firstColumnQuarter');
            },
            reportListColumnList() {
                if (this.reportListType === "SALES_BY_DATE") {
                    return [
                        {key: "displayName", label: this.reportListFirstColumnLabel, type: "text"},
                        {key: "subtotalMoney", label: this.$t('report.columnSubTotal'), type: "money"},
                        {key: "taxMoney", label: this.$t('report.columnTax'), type: "money"},
                        {key: "discountMoney", label: this.$t('report.columnDiscount'), type: "money"},
                        {key: "totalMoney", label: this.$t('report.columnTotal'), type: "money"},
                        {key: "amountPaid", label: this.$t('report.columnAmountPaid'), type: "money"},
                        {key: "amountOwed", label: this.$t('report.columnAmountOwed'), type: "money"},
                        {key: "count", label: "#", type: "number"}
                    ];
                }
                if (this.reportListType === "SALES_BY_CLIENT") {
                    return [
                        {key: "clientName", label: this.$t('report.columnClient'), type: "text"},
                        {key: "subtotalMoney", label: this.$t('report.columnSubTotal'), type: "money"},
                        {key: "taxMoney", label: this.$t('report.columnTax'), type: "money"},
                        {key: "discountMoney", label: this.$t('report.columnDiscount'), type: "money"},
                        {key: "totalMoney", label: this.$t('report.columnTotal'), type: "money"},
                        {key: "amountPaid", label: this.$t('report.columnAmountPaid'), type: "money"},
                        {key: "amountOwed", label: this.$t('report.columnAmountOwed'), type: "money"},
                        {key: "count", label: "#", type: "number"}
                    ];
                }
                if (this.reportListType === "SALES_BY_ITEM") {
                    return [
                        {key: "itemName", label: this.$t('report.columnItem'), type: "text"},
                        {key: "unitType", label: this.$t('report.columnUnitType'), type: "text"},
                        {key: "quantity", label: this.$t('report.columnQuantity'), type: "number"},
                        {key: "rateAvg", label: this.$t('report.columnRateAvg'), type: "money"},
                        {key: "subtotalMoney", label: this.$t('report.columnSubTotal'), type: "money"},
                        {key: "taxMoney", label: this.$t('report.columnTax'), type: "money"},
                        {key: "totalMoney", label: this.$t('report.columnTotal'), type: "money"},
                        {key: "count", label: "#", type: "number"}
                    ];
                }
                if (this.reportListType === "SALES_JOURNAL") {
                    return [
                        {key: "invoiceDate", label: this.$t('report.columnInvoiceDate'), type: "date"},
                        {key: "invoiceNo", label: this.$t('report.columnInvoiceNo'), type: "text"},
                        {key: "clientName", label: this.$t('report.columnClient'), type: "text"},
                        {key: "subtotalMoney", label: this.$t('report.columnSubTotal'), type: "money"},
                        {key: "taxMoney", label: this.$t('report.columnTax'), type: "money"},
                        {key: "discountMoney", label: this.$t('report.columnDiscount'), type: "money"},
                        {key: "totalMoney", label: this.$t('report.columnTotal'), type: "money"},
                        {key: "amountPaid", label: this.$t('report.columnAmountPaid'), type: "money"},
                        {key: "amountOwed", label: this.$t('report.columnAmountOwed'), type: "money"}
                    ];
                }
                if (this.reportListType === "PAYMENTS_BY_DATE") {
                    return [
                        {key: "displayName", label: this.reportListFirstColumnLabel, type: "text"},
                        {key: "totalMoney", label: this.$t('report.columnTotal'), type: "money"},
                        {key: "count", label: "#", type: "number"}
                    ];
                }
                if (this.reportListType === "PAYMENTS_BY_CLIENT") {
                    return [
                        {key: "clientName", label: this.$t('report.columnClient'), type: "text"},
                        {key: "totalMoney", label: this.$t('report.columnTotal'), type: "money"},
                        {key: "count", label: "#", type: "number"}
                    ];
                }
                if (this.reportListType === "PAYMENTS_BY_TYPE") {
                    return [
                        {key: "type", label: this.$t('report.columnType'), type: "text"},
                        {key: "totalMoney", label: this.$t('report.columnTotal'), type: "money"},
                        {key: "count", label: "#", type: "number"}
                    ];
                }
                if (this.reportListType === "PAYMENTS_JOURNAL") {
                    return [
                        {key: "paymentDate", label: this.$t('report.columnPaymentDate'), type: "date"},
                        {key: "invoiceDate", label: this.$t('report.columnInvoiceDate'), type: "date"},
                        {key: "invoiceNo", label: this.$t('report.columnInvoiceNo'), type: "text"},
                        {key: "clientName", label: this.$t('report.columnClient'), type: "text"},
                        {key: "totalMoney", label: this.$t('report.columnTotal'), type: "money"},
                        {key: "type", label: this.$t('report.columnType'), type: "text"}
                    ];
                }
                if (this.reportListType === "EXPENSE_BY_CATEGORY") {
                    return [
                        {key: "categoryName", label: this.$t('report.columnCategory'), type: "text"},
                        {key: "totalMoney", label: this.$t('report.columnTotal'), type: "money"},
                        {key: "taxMoney", label: this.$t('report.columnTax'), type: "money"},
                        {key: "count", label: "#", type: "number"}
                    ];
                }
                if (this.reportListType === "EXPENSE_JOURNAL") {
                    return [
                        {key: "expenseDate", label: this.$t('report.columnExpenseDate'), type: "date"},
                        {key: "categoryName", label: this.$t('report.columnCategory'), type: "text"},
                        {key: "merchant", label: this.$t('report.columnMerchant'), type: "text"},
                        {key: "description", label: this.$t('report.columnDescription'), type: "text"},
                        {key: "totalMoney", label: this.$t('report.columnTotal'), type: "money"},
                        {key: "taxMoney", label: this.$t('report.columnTax'), type: "money"}
                    ];
                }
                if (this.reportListType === "CUSTOMER_AGING") {
                    return [
                        {key: "clientName", label: this.$t('report.columnClient'), type: "text"},
                        {key: "totalMoney", label: this.$t('report.columnTotal'), type: "money"},
                        {key: "currentDue", label: this.$t('report.columnCurrentDue'), type: "money"},
                        {key: "days1To30Overdue", label: this.$t('report.column1To30DaysOverdue'), type: "money"},
                        {key: "days31To60Overdue", label: this.$t('report.column31To60DaysOverdue'), type: "money"},
                        {key: "days61To90Overdue", label: this.$t('report.column61To90DaysOverdue'), type: "money"},
                        {key: "days91PlusOverdue", label: this.$t('report.column91PlusDaysOverdue'), type: "money"}
                    ];
                }
                throw new Error("Invalid report list type");
            },
            reportListDonutChartList() {
                if (this.reportListType === "SALES_BY_CLIENT" || this.reportListType === "SALES_BY_ITEM" || this.reportListType === "PAYMENTS_BY_CLIENT") {
                    return this.reportListChartList.slice().sort((firstItem, secondItem) => {
                        return Number(secondItem.totalMoney) - Number(firstItem.totalMoney);
                    }).slice(0, 5);
                }
                return this.reportListChartList;
            },
            reportListChartLegendList() {
                let totalMoney = 0;
                for (let i = 0; i < this.reportListDonutChartList.length; i++) {
                    totalMoney += Number(this.reportListDonutChartList[i].totalMoney);
                }
                return this.reportListDonutChartList.map((item, index) => {
                    let percent = 0;
                    if (totalMoney > 0) {
                        percent = Math.round(Number(item.totalMoney) / totalMoney * 1000) / 10;
                    }
                    return {
                        index: index,
                        name: item.name,
                        key: item.key,
                        totalMoney: item.totalMoney,
                        percent: percent,
                        color: this.reportListColorList[index % this.reportListColorList.length]
                    };
                });
            },
            reportListDonutSliceList() {
                let totalMoney = 0;
                for (let i = 0; i < this.reportListDonutChartList.length; i++) {
                    totalMoney += Number(this.reportListDonutChartList[i].totalMoney);
                }
                if (totalMoney === 0) {
                    return [];
                }
                let currentAngle = -90;
                return this.reportListDonutChartList.map((item, index) => {
                    let sliceAngle = Number(item.totalMoney) / totalMoney * 360;
                    let startAngle = currentAngle;
                    let endAngle = currentAngle + sliceAngle;
                    currentAngle = endAngle;
                    return {
                        index: index,
                        key: item.key,
                        name: item.name,
                        totalMoney: item.totalMoney,
                        color: this.reportListColorList[index % this.reportListColorList.length],
                        path: this.getDonutSlicePath(100, 100, 70, 42, startAngle, endAngle)
                    };
                });
            },
            sortedReportListDetailList() {
                if (this.reportListSortKey === "") {
                    return this.reportListDetailList;
                }
                let column = this.reportListColumnList.find(item => item.key === this.reportListSortKey);
                if (column == null) {
                    throw new Error("Report list sort column is invalid");
                }
                return this.reportListDetailList.slice().sort((firstRow, secondRow) => {
                    let firstValue = this.getReportListSortValue(firstRow, column);
                    let secondValue = this.getReportListSortValue(secondRow, column);
                    if (column.type === "text") {
                        return this.reportListSortOrder === 1
                            ? firstValue.localeCompare(secondValue)
                            : secondValue.localeCompare(firstValue);
                    }
                    return this.reportListSortOrder === 1
                        ? firstValue - secondValue
                        : secondValue - firstValue;
                });
            },
            showReportListEmptyChart() {
                return this.reportListChartType === "BAR" || this.reportListChartType === "DONUT";
            },
            chartGridTemplateColumns() {
                return "repeat(" + this.displayPeriodList.length + ", minmax(0, 1fr))";
            },
            chartWidth() {
                if (this.selectedYear !== null || this.displayPeriodList.length <= 10) {
                    return "100%";
                }
                return (this.displayPeriodList.length * 10) + "%";
            },
            chartViewportWidth() {
                return "100%";
            },
            reportListChartGridTemplateColumns() {
                return "repeat(" + this.reportListChartList.length + ", minmax(0, 1fr))";
            },
            reportListChartWidth() {
                if (this.reportListTimePeriodType !== "ALL_TIME" || this.reportListChartList.length <= 10) {
                    return "100%";
                }
                return (this.reportListChartList.length * 10) + "%";
            },
            displayPeriodList() {
                return this.reportBarList;
            },
            displayPeriodTableList() {
                if (this.selectedYear === null || this.groupType !== "MONTH") {
                    return this.displayPeriodList;
                }
                let tableEndMonthValue = this.getSelectedFiscalTableEndMonthValue();
                if (tableEndMonthValue === null) {
                    return [];
                }
                return this.displayPeriodList.filter(period => {
                    return this.getMonthValue(period.periodYear, period.periodMonth) <= tableEndMonthValue;
                });
            },
            totalOutstandingBalance() {
                let totalMoney = 0;
                for (let i = 0; i < this.outstandingBalanceList.length; i++) {
                    totalMoney += Number(this.outstandingBalanceList[i].totalOutstandingBalance);
                }
                return totalMoney;
            },
            displayOutstandingBalanceList() {
                return this.outstandingBalanceList.slice().sort((firstClient, secondClient) => {
                    return Number(secondClient.totalOutstandingBalance) - Number(firstClient.totalOutstandingBalance);
                }).slice(0, 5);
            },
            clientPieSliceList() {
                let totalMoney = 0;
                for (let i = 0; i < this.clientSalesList.length; i++) {
                    totalMoney += Number(this.clientSalesList[i].totalMoney);
                }
                if (totalMoney === 0) {
                    return [];
                }
                let currentAngle = -90;
                let sliceList = [];
                for (let i = 0; i < this.clientSalesList.length; i++) {
                    let client = this.clientSalesList[i];
                    let sliceAngle = Number(client.totalMoney) / totalMoney * 360;
                    let startAngle = currentAngle;
                    let endAngle = currentAngle + sliceAngle;
                    sliceList.push({
                        innerUUID: client.innerUUID,
                        name: client.name,
                        totalMoney: client.totalMoney,
                        color: client.color,
                        path: this.getDonutSlicePath(100, 100, 70, 42, startAngle, endAngle)
                    });
                    currentAngle = endAngle;
                }
                return sliceList;
            },
            maxReportMoney() {
                let maxMoney = 0;
                for (let i = 0; i < this.displayPeriodList.length; i++) {
                    let totalMoney = parseFloat(this.displayPeriodList[i].totalMoney);
                    if (totalMoney > maxMoney) {
                        maxMoney = totalMoney;
                    }
                }
                return maxMoney;
            },
            maxReportListMoney() {
                let maxMoney = 0;
                for (let i = 0; i < this.reportListChartList.length; i++) {
                    let totalMoney = parseFloat(this.reportListChartList[i].totalMoney);
                    if (totalMoney > maxMoney) {
                        maxMoney = totalMoney;
                    }
                }
                return maxMoney;
            },
        },
        methods: {
            formatMoney(value) {
                return xiaoshudianfenge(value, this.userSetting);
            },
            formatReportListMoney(value) {
                return this.formatMoney(value);
            },
            formatReportListDate(value) {
                let date;
                if (typeof value === "number") {
                    date = new Date(value * 1000);
                } else if (/^\d+$/.test(String(value))) {
                    date = new Date(parseInt(value, 10) * 1000);
                } else {
                    date = new Date(value);
                }
                if (Number.isNaN(date.getTime())) {
                    throw new Error("Report list date is invalid");
                }
                let monthNameList = this.$t('report.monthNames').split(',');
                return monthNameList[date.getMonth()] + " " + date.getDate() + ", " + date.getFullYear();
            },
            formatReportListCell(row, column) {
                if (row === this.reportListTotalRow && column === this.reportListColumnList[0]) {
                    return this.$t('report.totalRowLabel');
                }
                if (row === this.reportListTotalRow && column.type === "text") {
                    return "";
                }
                let value = row[column.key];
                if (value === undefined || value === null || value === "") {
                    if (column.type === "money") {
                        return this.formatReportListMoney(0);
                    }
                    if (column.type === "number") {
                        return 0;
                    }
                    return "";
                }
                if (column.type === "money") {
                    return this.formatReportListMoney(value);
                }
                if (column.type === "number") {
                    return value;
                }
                if (column.type === "date") {
                    return this.formatReportListDate(value);
                }
                if (column.type === "text") {
                    return value;
                }
                throw new Error("Invalid report list column type");
            },
            getReportListSortValue(row, column) {
                let value = row[column.key];
                if (column.type === "text") {
                    if (value === undefined || value === null) {
                        return "";
                    }
                    return String(value);
                }
                if (column.type === "date") {
                    if (value === undefined || value === null || value === "") {
                        return 0;
                    }
                    if (typeof value === "number") {
                        return value;
                    }
                    if (/^\d+$/.test(String(value))) {
                        return parseInt(value, 10);
                    }
                    let date = new Date(value);
                    if (Number.isNaN(date.getTime())) {
                        throw new Error("Report list sort date is invalid");
                    }
                    return Math.floor(date.getTime() / 1000);
                }
                if (column.type === "money" || column.type === "number") {
                    if (value === undefined || value === null || value === "") {
                        return 0;
                    }
                    return Number(value);
                }
                throw new Error("Invalid report list sort column type");
            },
            changeReportListSort(column) {
                if (this.reportListSortKey !== column.key) {
                    this.reportListSortKey = column.key;
                    this.reportListSortOrder = 2;
                    return;
                }
                if (this.reportListSortOrder === 2) {
                    this.reportListSortOrder = 1;
                    return;
                }
                this.reportListSortOrder = 2;
            },
            startLoading() {
                this.loadingCount++;
                this.loading = true;
            },
            finishLoading() {
                this.loadingCount--;
                if (this.loadingCount === 0) {
                    this.loading = false;
                }
                if (this.loadingCount < 0) {
                    throw new Error("Report loading count is invalid");
                }
            },
            getBarHeight(value) {
                return this.getBarHeightByMax(value, this.maxReportMoney);
            },
            getReportListBarHeight(value) {
                return this.getBarHeightByMax(value, this.maxReportListMoney);
            },
            getBarHeightByMax(value, maxMoney) {
                if (parseFloat(value) <= 0 || maxMoney === 0) {
                    return "0px";
                }
                return Math.max(16, parseFloat(value) / maxMoney * 240) + "px";
            },
            getSaleTooltip(period) {
                return period.periodName + " | " + this.formatMoney(period.totalMoney) + " | " + period.invoiceCount + " " + this.$t('report.invoicesCountSuffix');
            },
            getReportListSaleTooltip(period) {
                return period.displayName + " | " + this.formatReportListMoney(period.totalMoney) + " | " + period.invoiceCount + " " + this.$t('report.invoicesCountSuffix');
            },
            getReportListChartLabel(period) {
                return period.displayName;
            },
            getClientSaleTooltip(client) {
                return client.name + " | " + this.formatMoney(client.totalMoney);
            },
            getPointOnCircle(centerX, centerY, radius, angle) {
                let angleInRadians = angle * Math.PI / 180;
                return {
                    x: centerX + radius * Math.cos(angleInRadians),
                    y: centerY + radius * Math.sin(angleInRadians)
                };
            },
            getDonutSlicePath(centerX, centerY, outerRadius, innerRadius, startAngle, endAngle) {
                if (endAngle - startAngle >= 360) {
                    endAngle = startAngle + 359.99;
                }
                let largeArcFlag = endAngle - startAngle > 180 ? 1 : 0;
                let outerStart = this.getPointOnCircle(centerX, centerY, outerRadius, startAngle);
                let outerEnd = this.getPointOnCircle(centerX, centerY, outerRadius, endAngle);
                let innerStart = this.getPointOnCircle(centerX, centerY, innerRadius, startAngle);
                let innerEnd = this.getPointOnCircle(centerX, centerY, innerRadius, endAngle);
                return [
                    "M", outerStart.x, outerStart.y,
                    "A", outerRadius, outerRadius, 0, largeArcFlag, 1, outerEnd.x, outerEnd.y,
                    "L", innerEnd.x, innerEnd.y,
                    "A", innerRadius, innerRadius, 0, largeArcFlag, 0, innerStart.x, innerStart.y,
                    "Z"
                ].join(" ");
            },
            showClientPieTooltip(client, event) {
                this.clientPieTooltip.content = this.getClientSaleTooltip(client);
                this.clientPieTooltip.visible = true;
                this.moveClientPieTooltip(event);
            },
            moveClientPieTooltip(event) {
                let chartWrap = event.currentTarget.closest(".donut-chart-wrap");
                if (chartWrap == null) {
                    throw new Error("Donut chart container is required");
                }
                let rect = chartWrap.getBoundingClientRect();
                this.clientPieTooltip.left = event.clientX - rect.left + 12;
                this.clientPieTooltip.top = event.clientY - rect.top + 12;
            },
            hideClientPieTooltip() {
                this.clientPieTooltip.visible = false;
            },
            scrollSaleChart(direction) {
                let chartScroll = this.$refs.saleChartScroll;
                if (chartScroll == null) {
                    throw new Error("Sale chart scroll container is required");
                }
                chartScroll.scrollLeft += direction * chartScroll.clientWidth;
            },
            handleSaleChartWheel(event) {
                if (this.selectedYear !== null) {
                    return;
                }
                let chartScroll = this.$refs.saleChartScroll;
                if (chartScroll == null) {
                    throw new Error("Sale chart scroll container is required");
                }
                let scrollDistance = Math.abs(event.deltaX) > Math.abs(event.deltaY) ? event.deltaX : event.deltaY;
                chartScroll.scrollLeft += scrollDistance;
                event.preventDefault();
            },
            scrollReportListChart(direction) {
                let chartScroll = this.$refs.reportListSaleChartScroll;
                if (chartScroll == null) {
                    throw new Error("Report list chart scroll container is required");
                }
                chartScroll.scrollLeft += direction * chartScroll.clientWidth;
            },
            handleReportListChartWheel(event) {
                if (this.reportListTimePeriodType !== "ALL_TIME") {
                    return;
                }
                let chartScroll = this.$refs.reportListSaleChartScroll;
                if (chartScroll == null) {
                    throw new Error("Report list chart scroll container is required");
                }
                let scrollDistance = Math.abs(event.deltaX) > Math.abs(event.deltaY) ? event.deltaX : event.deltaY;
                chartScroll.scrollLeft += scrollDistance;
                event.preventDefault();
            },
            getTaxYearStart() {
                if (this.userSetting == null || this.userSetting.taxYearStart == null || this.userSetting.taxYearStart === "") {
                    throw new Error("Tax year start is required");
                }
                let taxYearStartDate = new Date(parseInt(this.userSetting.taxYearStart, 10) * 1000);
                return {
                    month: taxYearStartDate.getMonth() + 1,
                    day: taxYearStartDate.getDate()
                };
            },
            getCurrentFiscalYearEnd() {
                let taxYearStart = this.getTaxYearStart();
                let currentDate = new Date();
                if (taxYearStart.month === 1 && taxYearStart.day === 1) {
                    return currentDate.getFullYear();
                }
                let currentYearStartDate = new Date(currentDate.getFullYear(), taxYearStart.month - 1, taxYearStart.day);
                if (currentDate.getTime() >= currentYearStartDate.getTime()) {
                    return currentDate.getFullYear() + 1;
                }
                return currentDate.getFullYear();
            },
            getFiscalYearDateRange(fiscalYearEnd) {
                let taxYearStart = this.getTaxYearStart();
                let startYear = fiscalYearEnd - 1;
                let endYear = fiscalYearEnd;
                if (taxYearStart.month === 1 && taxYearStart.day === 1) {
                    startYear = fiscalYearEnd;
                    endYear = fiscalYearEnd + 1;
                }
                return {
                    startDate: new Date(startYear, taxYearStart.month - 1, taxYearStart.day),
                    endDate: new Date(endYear, taxYearStart.month - 1, taxYearStart.day)
                };
            },
            getMonthValue(year, month) {
                return year * 12 + month;
            },
            getMonthValueFromDate(date) {
                return this.getMonthValue(date.getFullYear(), date.getMonth() + 1);
            },
            getPreviousDate(date) {
                return new Date(date.getFullYear(), date.getMonth(), date.getDate() - 1);
            },
            getPeriodName(year, month) {
                return year + "-" + String(month).padStart(2, "0");
            },
            getSelectedFiscalTableEndMonthValue() {
                let fiscalRange = this.getFiscalYearDateRange(this.selectedYear);
                let startMonthValue = this.getMonthValueFromDate(fiscalRange.startDate);
                let fiscalEndMonthValue = this.getMonthValueFromDate(this.getPreviousDate(fiscalRange.endDate));
                let currentMonthValue = this.getMonthValueFromDate(new Date());
                if (currentMonthValue < startMonthValue) {
                    return null;
                }
                if (currentMonthValue > fiscalEndMonthValue) {
                    return fiscalEndMonthValue;
                }
                return currentMonthValue;
            },
            changeDateIncrement(dateIncrement) {
                this.dateIncrement = dateIncrement;
                this.queryReportSaleStatistics();
            },
            changeReportTab(reportTab) {
                this.activeReportTab = reportTab;
                if (reportTab === "reportList") {
                    this.queryReportListData();
                }
            },
            getInvoiceListTimeQuery(year) {
                if (year === null) {
                    return {
                        timePeriodType: "ALL_TIME"
                    };
                }
                return {
                    timePeriodType: "TAX_YEAR",
                    year: year
                };
            },
            getFiscalYearEndByPeriod(period) {
                if (period.fiscalQuarter !== null && period.periodYear != null) {
                    return period.periodYear;
                }
                let periodDate = new Date(period.periodYear, period.periodMonth - 1, 1);
                let currentFiscalYear = this.getCurrentFiscalYearEnd();
                for (let year = currentFiscalYear + 5; year >= currentFiscalYear - 10; year--) {
                    let fiscalRange = this.getFiscalYearDateRange(year);
                    if (periodDate.getTime() >= fiscalRange.startDate.getTime() && periodDate.getTime() < fiscalRange.endDate.getTime()) {
                        return year;
                    }
                }
                throw new Error("Fiscal year is required for sale period");
            },
            goInvoiceListBySalePeriod(period) {
                let fiscalYear = this.selectedYear;
                if (fiscalYear === null) {
                    fiscalYear = this.getFiscalYearEndByPeriod(period);
                }
                this.$router.push({
                    path: "/invoice",
                    query: this.getInvoiceListTimeQuery(fiscalYear)
                });
            },
            goInvoiceListByClient(client) {
                let query = this.getInvoiceListTimeQuery(this.selectedYear);
                query.numberOrClient = client.name;
                query.clientUUID = client.innerUUID;
                this.$router.push({
                    path: "/invoice",
                    query: query
                });
            },
            goInvoiceListByReportClient(client) {
                if (this.reportListType !== "SALES_BY_CLIENT") {
                    return;
                }
                let query = this.getInvoiceListTimeQuery(this.selectedReportListYear);
                query.numberOrClient = client.name;
                query.clientUUID = client.key;
                this.$router.push({
                    path: "/invoice",
                    query: query
                });
            },
            goInvoiceListByReportClientRow(row) {
                if (this.reportListType !== "SALES_BY_CLIENT") {
                    return;
                }
                let query = this.getInvoiceListTimeQuery(this.selectedReportListYear);
                query.numberOrClient = row.clientName;
                query.clientUUID = row.clientUUID;
                this.$router.push({
                    path: "/invoice",
                    query: query
                });
            },
            changeReportListType() {
                this.reportListDateIncrement = "month";
                this.reportListSortKey = "";
                this.reportListSortOrder = "";
                this.queryReportListData();
            },
            queryReportData() {
                this.queryReportSaleStatistics();
                this.queryAnnualSalesByClient();
            },
            queryReportSaleStatistics() {
                this.startLoading();
                let params = {
                    type: 0,
                    companyUUID: getCurrentCompanyUUID(),
                    groupType: this.groupType
                };
                if (this.selectedYear !== null) {
                    params.year = this.selectedYear;
                }
                this.$axios.post('query_invoice_sale_statistics', {
                    params: params
                }).then(response => {
                    if (response.data.errorCode === 0) {
                        this.reportBarList = this.buildSalePeriodList(response.data.dataList);
                        this.reportSummary = this.buildSaleSummary(this.reportBarList);
                    }
                    this.finishLoading();
                }).catch(error => {
                    this.finishLoading();
                    throw error;
                })
            },
            queryReportListData() {
                this.startLoading();
                this.reportListLoaded = false;
                this.reportListSummary = {
                    reportType: this.reportListType,
                    currencyName: "",
                    summaryCount: 0,
                    summaryCountName: "",
                    summaryTotalMoney: 0
                };
                this.reportListChartType = "NONE";
                this.reportListChartList = [];
                this.reportListDetailList = [];
                this.reportListTotalRow = {};
                let params = {
                    reportType: this.reportListType,
                    companyUUID: getCurrentCompanyUUID()
                };
                if (this.showReportListTimePeriodFilter) {
                    params.timePeriodType = this.reportListTimePeriodType;
                }
                if (this.isInvoiceReportType(this.reportListType)) {
                    params.type = 0;
                }
                if (this.showReportListGroupTypeFilter) {
                    params.groupType = this.reportListGroupType;
                }
                if (this.selectedReportListYear !== null) {
                    params.year = this.selectedReportListYear;
                }
                if (this.reportListType === "CUSTOMER_AGING") {
                    params.nowTime = Math.floor(Date.now() / 1000);
                }
                this.$axios.post('query_report_data', {
                    params: params
                }).then(response => {
                    if (response.data.errorCode === 0) {
                        let reportData = response.data.objectData;
                        this.reportListSummary = {
                            reportType: reportData.reportType,
                            currencyName: reportData.currencyName,
                            summaryCount: reportData.summaryCount,
                            summaryCountName: reportData.summaryCountName,
                            summaryTotalMoney: reportData.summaryTotalMoney
                        };
                        this.reportListChartType = reportData.chartType;
                        this.reportListChartList = reportData.chartList;
                        this.reportListDetailList = reportData.dataList;
                        this.reportListTotalRow = reportData.totalRow;
                        this.reportListLoaded = true;
                    }
                    this.finishLoading();
                }).catch(error => {
                    this.finishLoading();
                    throw error;
                })
            },
            isInvoiceReportType(reportType) {
                return reportType === "SALES_BY_DATE"
                    || reportType === "SALES_BY_CLIENT"
                    || reportType === "SALES_BY_ITEM"
                    || reportType === "SALES_JOURNAL";
            },
            queryAnnualSalesByClient() {
                this.startLoading();
                let params = {
                    companyUUID: getCurrentCompanyUUID()
                };
                if (this.selectedYear !== null) {
                    params.year = this.selectedYear;
                }
                this.$axios.post('query_annual_sales_by_client', {
                    params: params
                }).then(response => {
                    if (response.data.errorCode === 0) {
                        this.clientSalesList = this.buildClientSalesList(response.data.dataList);
                    }
                    this.finishLoading();
                }).catch(error => {
                    this.finishLoading();
                    throw error;
                })
            },
            queryTotalOutstandingBalance() {
                this.startLoading();
                this.$axios.post('query_total_outstanding_balance', {
                    params: {
                        companyUUID: getCurrentCompanyUUID()
                    }
                }).then(response => {
                    if (response.data.errorCode === 0) {
                        this.outstandingBalanceList = response.data.dataList;
                    }
                    this.finishLoading();
                }).catch(error => {
                    this.finishLoading();
                    throw error;
                })
            },
            buildSalePeriodList(dataList) {
                if (this.selectedYear === null && this.groupType === "MONTH") {
                    return this.buildAllYearMonthPeriodList(dataList);
                }
                if (this.selectedYear === null && this.groupType === "QUARTER") {
                    return this.buildAllYearQuarterPeriodList(dataList);
                }
                if (this.selectedYear !== null && this.groupType === "MONTH") {
                    return this.buildSelectedFiscalMonthPeriodList(dataList);
                }
                if (this.selectedYear !== null && this.groupType === "QUARTER") {
                    let quarterMap = {};
                    for (let i = 0; i < dataList.length; i++) {
                        quarterMap[dataList[i].fiscalQuarter] = dataList[i];
                    }
                    let quarterList = [];
                    for (let quarter = 4; quarter >= 1; quarter--) {
                        if (quarterMap[quarter] == null) {
                            quarterList.push({
                                periodKey: this.selectedYear + "-Q" + quarter,
                                periodName: this.selectedYear + "-Q" + quarter,
                                title: this.selectedYear + "-Q" + quarter,
                                periodYear: this.selectedYear,
                                periodMonth: null,
                                fiscalQuarter: quarter,
                                invoiceCount: 0,
                                totalMoney: 0
                            });
                        } else {
                            quarterList.push({
                                periodKey: this.selectedYear + "-Q" + quarter,
                                periodName: this.selectedYear + "-Q" + quarter,
                                title: this.selectedYear + "-Q" + quarter,
                                periodYear: this.selectedYear,
                                periodMonth: quarterMap[quarter].periodMonth,
                                fiscalQuarter: quarter,
                                invoiceCount: quarterMap[quarter].invoiceCount,
                                totalMoney: quarterMap[quarter].totalMoney
                            });
                        }
                    }
                    return quarterList;
                }
                return dataList.map(item => {
                    return this.buildSalePeriod(item);
                });
            },
            buildAllYearMonthPeriodList(dataList) {
                if (dataList.length === 0) {
                    return [];
                }
                let periodMap = {};
                let minPeriodValue = null;
                let maxPeriodValue = null;
                for (let i = 0; i < dataList.length; i++) {
                    let item = dataList[i];
                    let periodValue = this.getMonthValue(item.periodYear, item.periodMonth);
                    periodMap[periodValue] = item;
                    if (minPeriodValue === null || periodValue < minPeriodValue) {
                        minPeriodValue = periodValue;
                    }
                    if (maxPeriodValue === null || periodValue > maxPeriodValue) {
                        maxPeriodValue = periodValue;
                    }
                }
                let periodList = [];
                for (let periodValue = maxPeriodValue; periodValue >= minPeriodValue; periodValue--) {
                    let periodYear = Math.floor((periodValue - 1) / 12);
                    let periodMonth = periodValue - periodYear * 12;
                    let periodName = this.getPeriodName(periodYear, periodMonth);
                    if (periodMap[periodValue] == null) {
                        periodList.push({
                            periodKey: periodName,
                            periodName: periodName,
                            title: periodName,
                            periodYear: periodYear,
                            periodMonth: periodMonth,
                            fiscalQuarter: null,
                            invoiceCount: 0,
                            totalMoney: 0
                        });
                    } else {
                        periodList.push(this.buildSalePeriod(periodMap[periodValue]));
                    }
                }
                return periodList;
            },
            buildSelectedFiscalMonthPeriodList(dataList) {
                let fiscalRange = this.getFiscalYearDateRange(this.selectedYear);
                let startMonthValue = this.getMonthValueFromDate(fiscalRange.startDate);
                let endMonthValue = this.getMonthValueFromDate(this.getPreviousDate(fiscalRange.endDate));
                let periodMap = {};
                for (let i = 0; i < dataList.length; i++) {
                    periodMap[dataList[i].periodName] = dataList[i];
                }
                let periodList = [];
                for (let periodValue = endMonthValue; periodValue >= startMonthValue; periodValue--) {
                    let periodYear = Math.floor((periodValue - 1) / 12);
                    let periodMonth = periodValue - periodYear * 12;
                    let periodName = this.getPeriodName(periodYear, periodMonth);
                    if (periodMap[periodName] == null) {
                        periodList.push({
                            periodKey: periodName,
                            periodName: periodName,
                            title: periodName,
                            periodYear: periodYear,
                            periodMonth: periodMonth,
                            fiscalQuarter: null,
                            invoiceCount: 0,
                            totalMoney: 0
                        });
                    } else {
                        periodList.push(this.buildSalePeriod(periodMap[periodName]));
                    }
                }
                return periodList;
            },
            buildAllYearQuarterPeriodList(dataList) {
                if (dataList.length === 0) {
                    return [];
                }
                let periodMap = {};
                let minPeriodValue = null;
                let maxPeriodValue = null;
                for (let i = 0; i < dataList.length; i++) {
                    let item = dataList[i];
                    let periodValue = item.periodYear * 4 + item.fiscalQuarter;
                    periodMap[periodValue] = item;
                    if (minPeriodValue === null || periodValue < minPeriodValue) {
                        minPeriodValue = periodValue;
                    }
                    if (maxPeriodValue === null || periodValue > maxPeriodValue) {
                        maxPeriodValue = periodValue;
                    }
                }
                let periodList = [];
                for (let periodValue = maxPeriodValue; periodValue >= minPeriodValue; periodValue--) {
                    let periodYear = Math.floor((periodValue - 1) / 4);
                    let fiscalQuarter = periodValue - periodYear * 4;
                    let periodName = periodYear + "-Q" + fiscalQuarter;
                    if (periodMap[periodValue] == null) {
                        periodList.push({
                            periodKey: periodName,
                            periodName: periodName,
                            title: periodName,
                            periodYear: periodYear,
                            periodMonth: null,
                            fiscalQuarter: fiscalQuarter,
                            invoiceCount: 0,
                            totalMoney: 0
                        });
                    } else {
                        periodList.push(this.buildSalePeriod(periodMap[periodValue]));
                    }
                }
                return periodList;
            },
            buildSalePeriod(item) {
                return {
                    periodKey: item.periodName,
                    periodName: item.periodName,
                    title: item.periodName,
                    periodYear: item.periodYear,
                    periodMonth: item.periodMonth,
                    fiscalQuarter: item.fiscalQuarter,
                    invoiceCount: item.invoiceCount,
                    totalMoney: item.totalMoney
                };
            },
            buildClientSalesList(dataList) {
                let sortedDataList = dataList.slice().sort((firstClient, secondClient) => {
                    return Number(secondClient.totalMoney) - Number(firstClient.totalMoney);
                }).slice(0, 5);
                let totalMoney = 0;
                for (let i = 0; i < sortedDataList.length; i++) {
                    totalMoney += Number(sortedDataList[i].totalMoney);
                }
                return sortedDataList.map((item, index) => {
                    let percent = 0;
                    if (totalMoney > 0) {
                        percent = Math.round(Number(item.totalMoney) / totalMoney * 1000) / 10;
                    }
                    return {
                        innerUUID: item.innerUUID,
                        name: item.name,
                        totalMoney: item.totalMoney,
                        percent: percent,
                        color: this.clientColorList[index % this.clientColorList.length]
                    };
                });
            },
            buildSaleSummary(periodList) {
                let totalInvoiceCount = 0;
                let totalMoney = 0;
                for (let i = 0; i < periodList.length; i++) {
                    totalInvoiceCount += Number(periodList[i].invoiceCount);
                    totalMoney += Number(periodList[i].totalMoney);
                }
                return {
                    totalInvoiceCount: totalInvoiceCount,
                    totalMoney: totalMoney
                };
            },
            escapeCsvValue(value) {
                let text = String(value);
                if (text.indexOf('"') >= 0 || text.indexOf(",") >= 0 || text.indexOf("\n") >= 0) {
                    return '"' + text.replace(/"/g, '""') + '"';
                }
                return text;
            },
            exportReportListCsv() {
                let header = this.reportListColumnList.map(column => {
                    return column.label;
                });
                let rowList = [header];
                for (let i = 0; i < this.sortedReportListDetailList.length; i++) {
                    let row = this.sortedReportListDetailList[i];
                    rowList.push(this.reportListColumnList.map(column => {
                        return this.formatReportListCell(row, column);
                    }));
                }
                rowList.push(this.reportListColumnList.map(column => {
                    return this.formatReportListCell(this.reportListTotalRow, column);
                }));
                let csvContent = rowList.map(row => {
                    return row.map(value => {
                        return this.escapeCsvValue(value);
                    }).join(",");
                }).join("\n");
                let blob = new Blob([csvContent], {type: "text/csv;charset=utf-8;"});
                let url = URL.createObjectURL(blob);
                let link = document.createElement("a");
                link.href = url;
                link.download = this.reportListType.toLowerCase() + ".csv";
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                URL.revokeObjectURL(url);
            }
        },
        created() {
            this.selectedYear = this.getCurrentFiscalYearEnd();
            this.selectedReportListYear = this.selectedYear;
        },
        mounted() {
            this.queryReportData();
            this.queryTotalOutstandingBalance();
        }
    }
</script>

<style scoped>
    /deep/ .el-header {
        background-color: #ffffff;
        font-size: 25px;
        font-weight: 700;
        height: 100px !important;
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding: 30px;
        border-bottom: 1px solid #E4E7ED;
    }

    .report-main {
        padding: 0;
        background: #F6F7F9;
    }

    .report-tabs {
        display: flex;
        align-items: flex-start;
        justify-content: space-between;
        padding: 26px 30px 0;
        background: #ffffff;
        border-bottom: 1px solid #E4E7ED;
    }

    .report-tab-list {
        display: flex;
        gap: 26px;
    }

    .report-tab {
        padding: 0 0 14px;
        color: #5F6368;
        font-size: 16px;
        font-weight: 600;
        background: transparent;
        border: none;
        border-bottom: 4px solid transparent;
        cursor: pointer;
    }

    .report-tab.active {
        color: #111111;
        border-bottom-color: #FF4B1F;
    }

    .export-button {
        display: inline-flex;
        align-items: center;
        justify-content: space-between;
        width: 150px;
        height: 44px;
        padding: 0 22px;
        color: #ffffff;
        font-size: 16px;
        font-weight: 700;
        background: #3445FF;
        border: none;
        border-radius: 8px;
        cursor: pointer;
        transform: translateY(-14px);
    }

    .report-overview-section {
        padding-bottom: 24px;
    }

    .report-overview-grid {
        display: grid;
        grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
        gap: 144px;
    }

    .report-panel {
        min-width: 0;
    }

    .report-section {
        padding: 32px 30px;
        background: #ffffff;
        border-bottom: 12px solid #F0F2F5;
    }

    .report-section h2 {
        margin: 0 0 10px;
        color: #111111;
        font-size: 22px;
        font-weight: 700;
    }

    .report-section-header {
        display: flex;
        align-items: flex-start;
        justify-content: space-between;
    }

    .invoice-count {
        color: #111111;
        font-size: 15px;
        font-weight: 600;
    }

    .total-sales {
        margin-top: 6px;
        color: #000000;
        font-size: 36px;
        font-weight: 700;
    }

    .year-select {
        width: 150px;
    }

    .report-tabs .year-select {
        transform: translateY(-14px);
    }

    .report-mode-tabs {
        display: flex;
        justify-content: center;
        margin-top: 20px;
    }

    .mode-button {
        padding: 8px 14px;
        color: #5F6368;
        font-size: 15px;
        background: #F5F6F8;
        border: 1px solid #F0F1F3;
        cursor: pointer;
    }

    .mode-button.active {
        color: #ffffff;
        background: #333333;
        border-color: #333333;
    }

    .bar-chart-wrap {
        margin-top: 20px;
    }

    .bar-chart-actions {
        display: flex;
        justify-content: flex-end;
        gap: 8px;
        margin-bottom: 8px;
    }

    .bar-chart-scroll {
        max-width: 100%;
        overflow-x: auto;
        overflow-y: hidden;
    }

    .bar-chart {
        display: grid;
        gap: 3px;
        align-items: end;
        min-height: 310px;
        padding-bottom: 2px;
        position: relative;
    }

    .bar-chart:before {
        content: "";
        position: absolute;
        left: 0;
        right: 0;
        bottom: 38px;
        height: 1px;
        background: #DADDE3;
    }

    .bar-column {
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: flex-end;
        min-width: 0;
        position: relative;
        z-index: 1;
    }

    .bar-track {
        height: 260px;
        display: flex;
        align-items: flex-end;
        justify-content: center;
        width: 100%;
        cursor: pointer;
    }

    .bar-value {
        width: 72px;
        background: #217BD3;
        transition: height 0.18s ease;
    }

    .bar-label {
        height: 36px;
        padding-top: 10px;
        box-sizing: border-box;
        color: #737373;
        font-size: 14px;
    }

    .period-list {
        margin-top: 34px;
        max-height: 500px;
        overflow-y: auto;
    }

    .empty-report {
        display: flex;
        align-items: center;
        justify-content: center;
        min-height: 220px;
        margin-top: 20px;
        color: #737373;
        font-size: 15px;
        border: 1px dashed #DADDE3;
    }

    .period-row,
    .client-sales-row,
    .outstanding-row {
        display: flex;
        align-items: center;
        justify-content: space-between;
        min-height: 50px;
        border-bottom: 1px solid #E0E2E6;
        color: #111111;
        font-size: 16px;
        font-weight: 600;
    }

    .period-row,
    .report-list-donut-row-item,
    .report-list-clickable-row {
        cursor: pointer;
    }

    .client-chart-row {
        display: grid;
        grid-template-columns: 220px minmax(220px, 1fr);
        gap: 34px;
        align-items: center;
    }

    .donut-chart {
        width: 200px;
        height: 200px;
    }

    .donut-chart-wrap {
        width: 200px;
        height: 200px;
        position: relative;
    }

    .donut-empty {
        fill: none;
        stroke-width: 56;
        stroke: #E5E7EB;
    }

    .donut-slice {
        cursor: pointer;
    }

    .client-pie-tooltip {
        position: absolute;
        z-index: 10;
        padding: 8px 10px;
        color: #ffffff;
        font-size: 13px;
        font-weight: 600;
        white-space: nowrap;
        background: rgba(51, 51, 51, 0.94);
        border-radius: 4px;
        pointer-events: none;
    }

    .client-sales-list {
        min-width: 0;
    }

    .client-sales-row,
    .outstanding-row {
        cursor: pointer;
    }

    .client-color {
        width: 4px;
        height: 36px;
        margin-right: 14px;
    }

    .client-name {
        flex: 1;
    }

    .outstanding-total {
        margin-top: 14px;
        color: #000000;
        font-size: 36px;
        font-weight: 700;
    }

    .outstanding-list {
        margin-top: 20px;
        max-height: 500px;
        overflow-y: auto;
    }

    .report-list-section {
        padding: 28px 30px 20px;
        background: #ffffff;
        border-bottom: 12px solid #F0F2F5;
    }

    .report-list-filters {
        display: flex;
        align-items: flex-start;
        gap: 28px;
    }

    .report-list-filter {
        width: 180px;
    }

    .report-list-filter span {
        display: block;
        margin-bottom: 4px;
        color: #333333;
        font-size: 13px;
        font-weight: 500;
    }

    .report-list-select {
        width: 100%;
    }

    /deep/ .report-list-select .el-input__inner {
        height: 32px;
        padding: 0 28px 0 0;
        color: #111111;
        font-size: 16px;
        font-weight: 700;
        background: transparent;
        border: none;
        border-bottom: 2px solid #D4D7DC;
        border-radius: 0;
    }

    /deep/ .report-list-select .el-input__suffix {
        right: 0;
    }

    .report-list-summary {
        margin-top: 28px;
    }

    .report-list-chart-wrap {
        margin-top: 42px;
    }

    .report-list-donut-row {
        display: grid;
        grid-template-columns: 300px minmax(360px, 1fr);
        gap: 72px;
        align-items: center;
        margin-top: 54px;
        min-height: 300px;
    }

    .report-list-donut-wrap {
        width: 260px;
        height: 260px;
    }

    .report-list-donut {
        display: block;
        width: 260px;
        height: 260px;
    }

    .report-list-donut-legend {
        min-width: 0;
    }

    .report-list-donut-row-item {
        display: grid;
        grid-template-columns: 4px minmax(180px, 1fr) minmax(140px, auto);
        gap: 14px;
        align-items: center;
        min-height: 50px;
        border-bottom: 1px solid #E0E2E6;
        color: #111111;
        font-size: 16px;
    }

    .report-detail-section {
        padding: 28px 30px 48px;
        background: #ffffff;
    }

    .report-detail-table-wrap {
        overflow-x: auto;
    }

    .report-detail-table {
        width: 100%;
        min-width: 1100px;
        border-collapse: collapse;
        color: #111111;
        table-layout: fixed;
    }

    .report-detail-table th,
    .report-detail-table td {
        padding: 20px 18px;
        text-align: right;
        border-bottom: 2px solid #DADDE3;
        font-size: 16px;
        white-space: nowrap;
    }

    .report-detail-table th {
        color: #666D76;
        font-weight: 500;
        border-bottom-color: #666D76;
        cursor: pointer;
    }

    .report-detail-table th:first-child,
    .report-detail-table td:first-child {
        width: 220px;
        text-align: left;
    }

    .report-detail-table td {
        font-weight: 500;
    }

    .report-detail-table td:first-child {
        font-weight: 700;
    }

    .report-list-clickable-row {
        cursor: pointer;
    }

    .report-detail-table th:last-child,
    .report-detail-table td:last-child {
        width: 48px;
    }

    .sales-journal-table th:first-child,
    .sales-journal-table td:first-child {
        width: 110px;
    }

    .sales-journal-table th:nth-child(2),
    .sales-journal-table td:nth-child(2) {
        width: 110px;
    }

    .sales-journal-table th:nth-child(5),
    .sales-journal-table td:nth-child(5),
    .sales-journal-table th:nth-child(6),
    .sales-journal-table td:nth-child(6),
    .sales-journal-table th:nth-child(7),
    .sales-journal-table td:nth-child(7) {
        width: 110px;
    }

    .sales-journal-table th:nth-child(8),
    .sales-journal-table td:nth-child(8),
    .sales-journal-table th:nth-child(9),
    .sales-journal-table td:nth-child(9) {
        width: 150px;
    }

    .payments-journal-table th:nth-child(1),
    .payments-journal-table td:nth-child(1),
    .payments-journal-table th:nth-child(2),
    .payments-journal-table td:nth-child(2),
    .payments-journal-table th:nth-child(3),
    .payments-journal-table td:nth-child(3) {
        width: 110px;
    }

    .payments-journal-table th:nth-child(4),
    .payments-journal-table td:nth-child(4) {
        width: 300px;
    }

    .payments-journal-table th:nth-child(5),
    .payments-journal-table td:nth-child(5) {
        width: 220px;
    }

    .payments-journal-table th:nth-child(6),
    .payments-journal-table td:nth-child(6) {
        width: 160px;
    }

    .customer-aging-table {
        min-width: 1480px;
    }

    .customer-aging-table .customer-aging-client-col {
        width: 160px;
    }

    .customer-aging-table th:first-child,
    .customer-aging-table td:first-child {
        width: 160px;
    }

    .customer-aging-table th:last-child,
    .customer-aging-table td:last-child {
        width: 220px;
    }

    @media (max-width: 1100px) {
        .report-overview-grid {
            grid-template-columns: 1fr;
        }

        .report-list-filters {
            flex-wrap: wrap;
        }
    }
</style>
